// src/systems/board.ts
//
// Landscape board layout (Phase 21 BOARD-21 revised; row-swap in Phase 22;
//                          4-subcell dozens in current phase):
//   Row 0: [D1-0][D1-1][D1-2][D1-3][D2-0][D2-1][D2-2][D2-3][D3-0][D3-1][D3-2][D3-3]
//          ← 4 subcells per dozen (3 numbers each), 1 col each — NO aliases needed
//   Row 1: [ 0 ][ 3 ][ 6 ][ 9 ][12][15][18][21][24][27][30][33][36][ C3 ]
//   Row 2: [ 0 ][ 2 ][ 5 ][ 8 ][11][14][17][20][23][26][29][32][35][ C2 ]
//   Row 3: [ 0 ][ 1 ][ 4 ][ 7 ][10][13][16][19][22][25][28][31][34][ C1 ]
//   Row 4: [lo-L][lo-R][ev-L][ev-R][rd-L][rd-R][bk-L][bk-R][od-L][od-R][hi-L][hi-R]
//          ← 12 outside-sub cells (2 per parent × 6 even-money bets), NO aliases
//
// col 0  = zero (spans rows 1-3; aliased at rows 2,3 for chess access)
// col 1-12 = numbers in rows 1-3; dozen-sub in row 0; outside-sub in row 4
// col 13 = column bets (C3@row1, C2@row2, C1@row3)
//
// Grid alias count = 2 (zero rows 2-3 only — dozen aliases removed)

import type { BoardCell, BoardLayout, BetType, SlotNumber, OutsideBetKind } from '../state/types';
import { RED_NUMBERS } from './wheel';

const ROWS = 5;
const COLS = 14; // col 0=zero, cols 1-12=numbers, col 13=col-bets

/**
 * Phase 21 (BOARD-21): canonical sub-cell number sets per parent bet.
 * Each parent splits into LEFT (first 9 sorted) and RIGHT (last 9 sorted) halves.
 * Halves are non-overlapping; union equals the parent's full coverage.
 *
 * Per revised Phase 21 scope: payout stays 1:1 (NOT proportionally scaled).
 * The trade-off: each sub-cell costs less expected payout per unit coverage,
 * but is the only way for chess fiches to target a half-set.
 */
export function buildSubCellNumberSets(): Record<
  OutsideBetKind,
  { left: SlotNumber[]; right: SlotNumber[] }
> {
  const ALL_RED = [...RED_NUMBERS].sort((a, b) => a - b) as SlotNumber[];
  const ALL_BLACK = Array.from({ length: 36 }, (_, i) => (i + 1) as SlotNumber)
    .filter((n) => !RED_NUMBERS.has(n))
    .sort((a, b) => a - b);

  const range = (start: number, end: number): SlotNumber[] =>
    Array.from({ length: end - start + 1 }, (_, i) => (start + i) as SlotNumber);

  const lowAll = range(1, 18);
  const highAll = range(19, 36);
  const evenAll = lowAll.concat(highAll).filter((n) => n % 2 === 0);
  const oddAll = lowAll.concat(highAll).filter((n) => n % 2 === 1);

  return {
    low: { left: lowAll.slice(0, 9), right: lowAll.slice(9) },
    high: { left: highAll.slice(0, 9), right: highAll.slice(9) },
    even: { left: evenAll.slice(0, 9), right: evenAll.slice(9) },
    odd: { left: oddAll.slice(0, 9), right: oddAll.slice(9) },
    red: { left: ALL_RED.slice(0, 9), right: ALL_RED.slice(9) },
    black: { left: ALL_BLACK.slice(0, 9), right: ALL_BLACK.slice(9) },
  };
}

/**
 * Dozen sub-cell number sets.
 * Each dozen covers 12 numbers split into 4 subcells of 3 numbers each,
 * one subcell per grid column (4 columns per dozen — no alias needed):
 *   D1 numbers 1-12:  sub0=[1-3]  sub1=[4-6]  sub2=[7-9]  sub3=[10-12]
 *   D2 numbers 13-24: sub0=[13-15] sub1=[16-18] sub2=[19-21] sub3=[22-24]
 *   D3 numbers 25-36: sub0=[25-27] sub1=[28-30] sub2=[31-33] sub3=[34-36]
 */
export function buildDozenSubNumberSets(): Record<
  1 | 2 | 3,
  [SlotNumber[], SlotNumber[], SlotNumber[], SlotNumber[]]
> {
  const range = (start: number, end: number): SlotNumber[] =>
    Array.from({ length: end - start + 1 }, (_, i) => (start + i) as SlotNumber);
  return {
    1: [range(1, 3), range(4, 6), range(7, 9), range(10, 12)],
    2: [range(13, 15), range(16, 18), range(19, 21), range(22, 24)],
    3: [range(25, 27), range(28, 30), range(31, 33), range(34, 36)],
  };
}

export function buildCanonicalBoardLayout(): BoardLayout {
  const grid: (BoardCell | null)[][] = Array.from({ length: ROWS }, () =>
    Array.from({ length: COLS }, () => null),
  );
  const cells: BoardCell[] = [];

  const push = (row: number, col: number, bet: BetType): BoardCell => {
    const cell: BoardCell = { row, col, bet };
    cells.push(cell);
    grid[row]![col] = cell;
    return cell;
  };

  // Row 4: outside-sub cells (Phase 21 BOARD-21 — revised scope).
  // Each of 6 even-money parents splits into 2 sub-cells, each occupying 1 grid col.
  // Layout: low-L,low-R, even-L,even-R, red-L,red-R, black-L,black-R, odd-L,odd-R, high-L,high-R
  // (matches v1 visual order at startCols 1, 3, 5, 7, 9, 11; sub-cells at startCol and startCol+1).
  const subSets = buildSubCellNumberSets();
  const subDefs: Array<{ parentKind: OutsideBetKind; startCol: number }> = [
    { parentKind: 'low', startCol: 1 },
    { parentKind: 'even', startCol: 3 },
    { parentKind: 'red', startCol: 5 },
    { parentKind: 'black', startCol: 7 },
    { parentKind: 'odd', startCol: 9 },
    { parentKind: 'high', startCol: 11 },
  ];
  subDefs.forEach(({ parentKind, startCol }) => {
    const { left, right } = subSets[parentKind];
    push(4, startCol, {
      kind: 'outside-sub',
      parentKind,
      numbers: left,
      payout: 1, // 1:1 unchanged from parent (revised scope)
    });
    push(4, startCol + 1, {
      kind: 'outside-sub',
      parentKind,
      numbers: right,
      payout: 1,
    });
  });

  // Rows 1-3: number grid. n = (col-1)*3 + (4-row)
  //   row=1 → n = multiples of 3 (3,6,...,36)
  //   row=2 → n = 3k-1 (2,5,...,35)
  //   row=3 → n = 3k-2 (1,4,...,34)
  for (let row = 1; row <= 3; row++) {
    for (let col = 1; col <= 12; col++) {
      const n = ((col - 1) * 3 + (4 - row)) as SlotNumber;
      push(row, col, { kind: 'straight', number: n });
    }
  }

  // Zero: primary at (row=1, col=0); aliased at rows 2 and 3 for chess reach.
  const zeroCell = push(1, 0, { kind: 'straight', number: 0 as SlotNumber });
  grid[2]![0] = zeroCell;
  grid[3]![0] = zeroCell;

  // Column bets at col=13 (rows 1-3).
  // C3 = column-3 numbers (3,6,...,36 = row 1)
  // C2 = column-2 numbers (2,5,...,35 = row 2)
  // C1 = column-1 numbers (1,4,...,34 = row 3)
  push(1, 13, { kind: 'column', which: 3 });
  push(2, 13, { kind: 'column', which: 2 });
  push(3, 13, { kind: 'column', which: 1 });

  // Row 0: dozen subcells (dozen-sub). Each dozen (D1/D2/D3) is divided into 4
  // equal subcells, each covering 3 numbers. 4 subcells × 1 col each = 4 cols
  // per dozen — no alias needed (all 4 columns are real cells).
  //   D1: sub0=col1, sub1=col2, sub2=col3, sub3=col4
  //   D2: sub0=col5, sub1=col6, sub2=col7, sub3=col8
  //   D3: sub0=col9, sub1=col10, sub2=col11, sub3=col12
  const dozenSubs = buildDozenSubNumberSets();
  const dozenDefs: Array<{ which: 1 | 2 | 3; startCol: number }> = [
    { which: 1, startCol: 1 },
    { which: 2, startCol: 5 },
    { which: 3, startCol: 9 },
  ];
  dozenDefs.forEach(({ which, startCol }) => {
    const [sub0nums, sub1nums, sub2nums, sub3nums] = dozenSubs[which];
    push(0, startCol, { kind: 'dozen-sub', parentWhich: which, subIdx: 0, numbers: sub0nums });
    push(0, startCol + 1, { kind: 'dozen-sub', parentWhich: which, subIdx: 1, numbers: sub1nums });
    push(0, startCol + 2, { kind: 'dozen-sub', parentWhich: which, subIdx: 2, numbers: sub2nums });
    push(0, startCol + 3, { kind: 'dozen-sub', parentWhich: which, subIdx: 3, numbers: sub3nums });
  });

  return { cells, grid, dims: { rows: ROWS, cols: COLS } };
}

export const CANONICAL_BOARD_LAYOUT: BoardLayout = Object.freeze(
  buildCanonicalBoardLayout(),
) as BoardLayout;

// Phase 19 (FICHE-19): orthogonal-adjacency lookup for the Cell Swapper
// fiche. Returns at most 4 distinct cells (up/down/left/right). De-aliases
// multi-span outside-bet cells via Set-based dedup, and excludes the input
// cell itself (the alias guard).
const ORTHO_DELTAS: readonly [number, number][] = [
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
];
/**
 * Returns all subcells reachable from `cell` via repeated 4-connected traversal
 * through cells of the same parent (same dozen parentWhich or same outside-sub
 * parentKind). The starting cell is included. Non-subcell input returns [cell].
 *
 * Used by placement validation: placing on any cell in this component blocks
 * placing on any other cell in the same component.
 */
export function subcellConnectedComponent(layout: BoardLayout, cell: BoardCell): BoardCell[] {
  const bet = cell.bet;
  if (bet.kind !== 'dozen-sub' && bet.kind !== 'outside-sub') return [cell];

  const isSameParent = (b: BoardCell): boolean => {
    if (bet.kind === 'dozen-sub' && b.bet.kind === 'dozen-sub')
      return bet.parentWhich === b.bet.parentWhich;
    if (bet.kind === 'outside-sub' && b.bet.kind === 'outside-sub')
      return bet.parentKind === b.bet.parentKind;
    return false;
  };

  const visited = new Set<BoardCell>([cell]);
  const queue: BoardCell[] = [cell];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    for (const [dr, dc] of ORTHO_DELTAS) {
      const r = cur.row + dr;
      const c = cur.col + dc;
      if (r < 0 || r >= layout.dims.rows || c < 0 || c >= layout.dims.cols) continue;
      const nb = layout.grid[r]?.[c];
      if (!nb || nb === cur || visited.has(nb)) continue;
      if (isSameParent(nb)) {
        visited.add(nb);
        queue.push(nb);
      }
    }
  }
  return [...visited];
}

export function orthogonalNeighbors(layout: BoardLayout, cell: BoardCell): BoardCell[] {
  const seen = new Set<BoardCell>();
  const result: BoardCell[] = [];
  for (const [dr, dc] of ORTHO_DELTAS) {
    const r = cell.row + dr;
    const c = cell.col + dc;
    if (r < 0 || r >= layout.dims.rows) continue;
    if (c < 0 || c >= layout.dims.cols) continue;
    const neighbor = layout.grid[r]?.[c];
    if (!neighbor) continue;
    if (neighbor === cell) continue; // alias guard
    if (seen.has(neighbor)) continue;
    seen.add(neighbor);
    result.push(neighbor);
  }
  return result;
}
